/**
 * useMidCycleProrationPreview
 * ---------------------------------------------------------------------------
 * Hook de PRÉVIA do ajuste pro-rata mid-cycle ao editar uma recorrência.
 * Chama a RPC `preview_mid_cycle_proration` (Fase C do projeto Pro-Rata em
 * Edição de Recorrência) sem persistir nada — apenas para mostrar ao usuário
 * o impacto financeiro antes do submit.
 *
 * RELEASE:
 *   2026-06-17 v1.0.0 - Versão inicial (Fase C).
 *
 * Estratégia:
 *  - Debounce de 400ms sobre os itens informados (evita flood de chamadas
 *    enquanto o usuário ajusta quantidade/valor).
 *  - Desativado quando `enabled=false` (modal fechado ou Zona Sensível
 *    bloqueada).
 *  - Retorno totalmente tipado em torno do JSON devolvido pela RPC.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useMidCycleProrationPreview');

/** Item enviado para a RPC (apenas campos relevantes ao cálculo). */
export interface PreviewItemInput {
  product_id: string;
  descricao?: string | null;
  quantidade: number;
  valor_unitario: number;
  /** Desconto total em R$ (já consolidado a partir de % ou fixo). */
  desconto: number;
}

export interface PreviewDiffRow {
  product_id: string;
  descricao: string | null;
  old_total: number;
  new_total: number;
  delta: number;
  delta_pro_rata: number;
}

export interface PreviewConfig {
  enabled: boolean;
  apply_on_subscription_edit: boolean;
  apply_on_plan_change: boolean;
  subscription_edit_mode: 'inherit' | 'charge_immediately' | 'add_to_next_invoice' | 'none';
  mode_effective: 'charge_immediately' | 'add_to_next_invoice' | 'create_credit_note' | 'none';
  min_proration_amount: number;
  // v1.3.0
  auto_apply_customer_credit?: boolean;
  customer_credit_prompt_mode?: 'auto' | 'ask';
}

export interface PreviewCiclo {
  id: string;
  ciclo_numero: number;
  status: string;
  periodo_inicio: string;
  periodo_fim: string;
  dias_totais: number;
  dias_restantes: number;
  pro_rata_factor: number;
}

/**
 * v1.0.3 — bloco com saldo de crédito disponível do cliente e previsão de
 * consumo/geração de crédito ao salvar a recorrência. Inclui politica_consumo
 * para a UI decidir se renderiza checkbox de confirmação.
 */
export interface PreviewCreditoItem {
  id: string;
  origem: string;
  valor: number;
  valor_usado: number;
  saldo: number;
  status: string;
  expira_em: string | null;
  assinatura_id: string | null;
  created_at: string;
}

export interface PreviewCreditosCliente {
  saldo_disponivel: number;
  lista_disponivel: PreviewCreditoItem[];
  /** Só >0 quando política permite e há crédito + cobrança a gerar. */
  consumo_previsto: number;
  /** Só >0 quando action='credit' (downgrade). */
  credito_a_gerar: number;
  saldo_apos_operacao: number;
  /** v1.0.3 — 'auto' = sempre consome; 'ask' = pede confirmação; 'disabled' = nunca consome. */
  politica_consumo?: 'auto' | 'ask' | 'disabled';
}

export interface MidCycleProrationPreview {
  applicable: boolean;
  reason: string | null;
  config: PreviewConfig;
  ciclo: PreviewCiclo | null;
  diff: PreviewDiffRow[];
  totals: {
    delta_bruto: number;
    delta_pro_rata: number;
    abs_pro_rata: number;
    ignored_by_min: boolean;
  };
  action: 'none' | 'charge' | 'credit';
  summary: string;
  /** Opcional para compat com versões antigas da RPC. */
  creditos_cliente?: PreviewCreditosCliente | null;
}

interface UseMidCycleProrationPreviewArgs {
  assinaturaId: string | null;
  itens: PreviewItemInput[];
  enabled: boolean;
}

/** Debounce simples para a lista de itens. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function useMidCycleProrationPreview({
  assinaturaId,
  itens,
  enabled,
}: UseMidCycleProrationPreviewArgs) {
  // Serializa a lista para que o debounce reaja apenas a mudanças reais.
  const itensKey = JSON.stringify(
    itens.map((i) => ({
      p: i.product_id,
      q: i.quantidade,
      v: i.valor_unitario,
      d: i.desconto,
    })),
  );
  const debouncedKey = useDebouncedValue(itensKey, 400);

  return useQuery<MidCycleProrationPreview | null>({
    queryKey: ['mid-cycle-proration-preview', assinaturaId, debouncedKey],
    enabled: Boolean(enabled && assinaturaId && itens.length > 0),
    staleTime: 15_000,
    queryFn: async () => {
      if (!assinaturaId) return null;
      const payloadItens = itens.map((i) => ({
        product_id: i.product_id,
        descricao: i.descricao ?? null,
        quantidade: i.quantidade,
        valor_unitario: i.valor_unitario,
        desconto: i.desconto,
      }));
      const { data, error } = await supabase.rpc('preview_mid_cycle_proration', {
        p_assinatura_id: assinaturaId,
        p_new_itens: payloadItens as unknown as never,
      });
      if (error) {
        logger.warn('preview rpc error', { error });
        throw error;
      }
      return data as unknown as MidCycleProrationPreview;
    },
  });
}
